import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Link2, Quote, Unlink } from "lucide-react";

const Btn = ({ active, onClick, title, children, testid }) => (
  <button type="button" data-testid={testid} title={title} onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${active ? "bg-navy text-white" : "text-slate-500 hover:bg-slate-100"}`}>
    {children}
  </button>
);

export default function RichTextEditor({ value, onChange, testId }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer" } }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html === "<p></p>" ? "" : html);
    },
  });

  // Sync external value changes (e.g. switching records) into the editor.
  useEffect(() => {
    if (editor && value !== editor.getHTML() && !editor.isFocused) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (use /services/payroll for internal pages):", prev);
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden bg-white" data-testid={testId}>
      <div className="flex items-center gap-1 flex-wrap border-b border-slate-100 bg-slate-50 px-2 py-1.5">
        <Btn testid="rte-bold" title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={15} /></Btn>
        <Btn testid="rte-italic" title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={15} /></Btn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <Btn testid="rte-h2" title="Heading" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={15} /></Btn>
        <Btn testid="rte-h3" title="Sub-heading" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 size={15} /></Btn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <Btn testid="rte-ul" title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={15} /></Btn>
        <Btn testid="rte-ol" title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={15} /></Btn>
        <Btn testid="rte-quote" title="Quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={15} /></Btn>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <Btn testid="rte-link" title="Add link" active={editor.isActive("link")} onClick={setLink}><Link2 size={15} /></Btn>
        <Btn testid="rte-unlink" title="Remove link" active={false} onClick={() => editor.chain().focus().unsetLink().run()}><Unlink size={15} /></Btn>
      </div>
      <EditorContent editor={editor} className="rte-content px-4 py-3 min-h-[160px] text-sm text-slate-700 focus:outline-none" />
    </div>
  );
}
